import {Component, OnDestroy, OnInit} from '@angular/core';
import {SuggestionListsService} from '../../services/suggestion-lists.service';
import {UserService} from '../../../../../services/user.service';
import {ComponentBackgroundService} from '../../../../../services/component-background.service';
import {AdminService} from '../../../../../services/admin.service';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import { SortTableService } from '../../../../../services/sort-table.service';
import { AppConstants } from '../../../../views/view-user-profile/app.constants';
import { SuggestionPageResponse } from 'app/models/suggestion-page-response.model';

const EMPTY_PAGE_COUNT = 1;

@Component({
  selector: 'app-for-review-suggestion-lists',
  templateUrl: './for-review-suggestion-lists.component.html',
  styleUrls: ['./for-review-suggestion-lists.component.css']
})
export class ForReviewSuggestionListsComponent implements OnInit, OnDestroy {
  private readonly ROLE_HEAD = 'HEAD';
  private readonly SL_DETAILS_ROUTE = '/dashboard/suggestion-list-details/';
  private readonly INITIAL_PAGE = 0;
  private readonly DEFAULT_PAGE_SIZE = 10;

  isLoaded = false;
  allReviewSLList: any[] = [];
  allReviewSLListClonedForSearching: any[] = [];
  internalServerError = false;
  thisUser: any;

  getGroupError = false;
  getSelfError = false;
  getHeadError = false;

  currentPage = this.INITIAL_PAGE;
  pageSize = this.DEFAULT_PAGE_SIZE;
  totalPages = this.INITIAL_PAGE;
  totalElements = this.INITIAL_PAGE;

  isLoadingNextPage = false;

  constructor(public suggestionListsService: SuggestionListsService,
              public componentBackgroundService: ComponentBackgroundService,
              private userService: UserService,
              private adminService: AdminService,
              private route: ActivatedRoute,
              private router: Router,
              private modalService: NgbModal,
              public sortTableService: SortTableService) {
  }

  ngOnDestroy(): void {
    this.suggestionListsService.showSuccessMsgForReviewList = false;
  }

  ngOnInit() {
    this.resetInitialState();
    this.userService.getSelf().subscribe(
      (res: any) => this.handleUserResponse(res),
      () => this.handleUserError()
    );
  }

  private resetInitialState() {
    this.isLoaded = false;
    this.internalServerError = false;
    this.getGroupError = false;
    this.getSelfError = false;
    this.getHeadError = false;
  }

  private handleUserResponse(res: any) {
    if (res.status === AppConstants.HTTP_STATUS.OK) {
      this.thisUser = res.body;
      this.processUserRoles();
    } else {
      this.handleUserError();
    }
  }

  private processUserRoles() {
    if (this.thisUser.roles && this.thisUser.roles.length > 0) {
      if (this.hasHeadRights()) {
        this.getSLForReview(this.thisUser.id);
      }
    } else {
      this.setRoleError();
    }
  }

  private hasHeadRights(): boolean {
    let hasRights = false;
    this.thisUser.roles.forEach(role => {
      if (role === this.ROLE_HEAD) {
        hasRights = true;
      }
    });
    return hasRights;
  }

  private setRoleError() {
    this.internalServerError = true;
    this.getGroupError = false;
    this.getSelfError = false;
    this.getHeadError = true;
  }

  private handleUserError() {
    this.internalServerError = true;
    this.getGroupError = false;
    this.getSelfError = true;
    this.getHeadError = false;
    this.isLoaded = true;
  }

  getSLForReview(headID, page: number = this.INITIAL_PAGE) {
    this.isLoadingNextPage = true;
    this.allReviewSLList = [];
    this.currentPage = page;

    this.suggestionListsService.getSLForReviewByHeadIDWithPagination(headID, page, this.pageSize)
      .subscribe(
        (response: any) => this.handleReviewListResponse(response, page),
        (error) => this.handleReviewListError(error)
      );
  }

  private handleReviewListResponse(response: any, page: number) {
    if (response.status === AppConstants.HTTP_STATUS.OK) {
      this.updateListData(response.body, page);
      this.setListSuccessState();
    } else {
      this.handleReviewListError(null);
    }
  }

  private updateListData(pageResponse: SuggestionPageResponse<Object>, page: number) {
    const newLists = (pageResponse && pageResponse.suggestions) ? pageResponse.suggestions : [];
    this.allReviewSLList.push(...newLists);
    
    this.currentPage = (pageResponse && pageResponse.currentPage !== undefined) ? pageResponse.currentPage : page;
    this.totalPages = (pageResponse && pageResponse.totalPages !== undefined) ? pageResponse.totalPages : EMPTY_PAGE_COUNT;
    this.totalElements = (pageResponse && pageResponse.totalElements !== undefined) ? pageResponse.totalElements : this.INITIAL_PAGE;
    
    this.allReviewSLListClonedForSearching = [...this.allReviewSLList];
  }

  private setListSuccessState() {
    this.isLoaded = true;
    this.isLoadingNextPage = false;
    this.internalServerError = false;
  }

  private handleReviewListError(error: any) {
    if (error) {
      console.error('Error loading review suggestions:', error);
    }
    this.internalServerError = true;
    this.getGroupError = true;
    this.getSelfError = false;
    this.getHeadError = false;
    this.isLoaded = true;
    this.isLoadingNextPage = false;
  }

  openSLDetails(list) {
    window.open(this.SL_DETAILS_ROUTE + list.id, '_blank');
  }

  searchText(value) {
    if (value) {
      const regexp = new RegExp(value, 'i');
      this.allReviewSLListClonedForSearching = [...this.allReviewSLList]
        .filter(elem => regexp.test(elem['description']));
    } else {
      this.allReviewSLListClonedForSearching = [...this.allReviewSLList];
    }
  }

  loadNextPage() {
    if (!this.isLoadingNextPage && this.hasNextPage()) {
      this.getSLForReview(this.thisUser.id, this.currentPage + 1);
    }
  }

  loadPreviousPage() {
    if (!this.isLoadingNextPage && this.currentPage > this.INITIAL_PAGE) {
      this.getSLForReview(this.thisUser.id, this.currentPage - 1);
    }
  }

  loadNext5Pages() {
    if (!this.isLoadingNextPage) {
      const targetPage = Math.min(this.currentPage + 5, this.totalPages - 1);
      if (targetPage > this.currentPage) {
        this.getSLForReview(this.thisUser.id, targetPage);
      }
    }
  }

  loadPrevious5Pages() {
    if (!this.isLoadingNextPage) {
      const targetPage = Math.max(this.currentPage - 5, this.INITIAL_PAGE);
      if (targetPage < this.currentPage) {
        this.getSLForReview(this.thisUser.id, targetPage);
      }
    }
  }

  hasNextPage(): boolean {
    return this.currentPage < (this.totalPages - 1);
  }

  hasPreviousPage(): boolean {
    return this.currentPage > this.INITIAL_PAGE;
  }

  hasNext5Pages(): boolean {
    return this.currentPage + 5 < this.totalPages;
  }

  hasPrevious5Pages(): boolean {
    return this.currentPage >= 5;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/suggestion-lists/components/for-review-suggestion-lists/for-review-suggestion-lists.component.ts